import React from 'react'
import { useTranslation } from 'react-i18next'

const PrivacyPolicyContent: React.FC = () => {
  const { i18n } = useTranslation()
  const isGerman = i18n.language === 'de'

  return (
    <div className="prose prose-sm max-w-none space-y-6">
      {isGerman ? (
        // German Content
        <>
          <section>
            <h2 className="text-lg font-semibold mb-3">1. Einführung</h2>
            <p>
              InsightFlow („wir", „unser" oder „uns") schützt Ihre Privatsphäre. Diese 
              Datenschutzerklärung erklärt, wie wir Ihre Daten sammeln, nutzen, teilen und 
              schützen, wenn Sie unsere Coaching-App und Dienste (der "Service") verwenden.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">2. Daten die wir sammeln</h2>
            
            <h3 className="text-md font-medium mb-2">2.1 Persönliche Daten</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name und E-Mail (nötig für Konto)</li>
              <li>Profildaten (Rolle, Skills, Einstellungen)</li>
              <li>Profilbilder (optional, mit Privatsphäre-Kontrollen)</li>
              <li>Sprach- und Benachrichtigungseinstellungen</li>
            </ul>

            <h3 className="text-md font-medium mb-2 mt-4">2.2 Coaching-Daten</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Reflexionen (Gedanken, Stimmung, Erkenntnisse)</li>
              <li>Ziele und Fortschritt</li>
              <li>Sitzungsnotizen und Termine</li>
              <li>Nachrichten in der App</li>
            </ul>

            <h3 className="text-md font-medium mb-2 mt-4">2.3 Technische Daten</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Gerät, System und Browser-Info</li>
              <li>IP-Adresse und grober Standort</li>
              <li>Nutzungsmuster</li>
              <li>Fehler- und Leistungsdaten</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">3. Wie wir Ihre Daten nutzen</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Für Coaching-Services</li>
              <li>Für Kommunikation zwischen Coaches und Coachees</li>
              <li>Für Fortschritt und Erkenntnisse</li>
              <li>Für wichtige Updates</li>
              <li>Für App-Verbesserungen</li>
              <li>Für Sicherheit gegen Missbrauch</li>
              <li>Für rechtliche Pflichten</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">4. Datenweitergabe und Offenlegung</h2>
            
            <h3 className="text-md font-medium mb-2">4.1 Coach-Coachee-Beziehungen</h3>
            <p>
              Ihre Coaching-Daten (Reflexionen, Ziele, Fortschritt) werden nur mit Coaches geteilt, mit denen 
              Sie sich ausdrücklich über unser Einladungssystem verknüpft haben. Diese Weitergabe ist für die 
              Coaching-Beziehung unerlässlich.
            </p>

            <h3 className="text-md font-medium mb-2 mt-4">4.2 Drittanbieter-Services</h3>
            <p>
              Wir verwenden Supabase für Datenspeicherung und Authentifizierung. Ihre Daten werden sicher in 
              EU-basierten Servern mit Sicherheitsmaßnahmen auf Unternehmensebene gespeichert.
            </p>

            <h3 className="text-md font-medium mb-2 mt-4">4.3 Gesetzliche Anforderungen</h3>
            <p>
              Wir können Ihre Informationen offenlegen, wenn dies gesetzlich vorgeschrieben ist, durch 
              Gerichtsbeschluss oder zum Schutz unserer Rechte, Sicherheit oder der Rechte anderer.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">5. Ihre Rechte (DSGVO-Konformität)</h2>
            <p>Unter der DSGVO und anderen Datenschutzgesetzen haben Sie das Recht auf:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Zugang:</strong> Eine Kopie Ihrer personenbezogenen Daten anzufordern</li>
              <li><strong>Berichtigung:</strong> Unrichtige oder unvollständige Daten zu korrigieren</li>
              <li><strong>Löschung:</strong> Die Löschung Ihrer personenbezogenen Daten zu verlangen</li>
              <li><strong>Portabilität:</strong> Ihre Daten in einem portablen Format zu erhalten</li>
              <li><strong>Einschränkung:</strong> Die Art der Datenverarbeitung zu begrenzen</li>
              <li><strong>Widerspruch:</strong> Gegen bestimmte Arten der Verarbeitung zu widersprechen</li>
              <li><strong>Einwilligung widerrufen:</strong> Die Einwilligung zur Datenverarbeitung zu widerrufen</li>
            </ul>
            <p className="mt-2">
              Um diese Rechte auszuüben, kontaktieren Sie uns unter <strong>privacy@insightflow.app</strong>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">6. Datensicherheit</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Ende-zu-Ende-Verschlüsselung für Datenübertragung</li>
              <li>Rollenbasierte Zugangskontrollen (Row Level Security)</li>
              <li>Regelmäßige Sicherheitsaudits und Updates</li>
              <li>Sichere EU-basierte Rechenzentren</li>
              <li>Multi-Faktor-Authentifizierung-Unterstützung</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">7. Datenspeicherung</h2>
            <p>
              Wir speichern Ihre personenbezogenen Daten nur so lange, wie es für die in dieser Richtlinie 
              beschriebenen Zwecke oder gesetzlich erforderlich ist. Wenn Sie Ihr Konto löschen, werden wir 
              Ihre personenbezogenen Daten innerhalb von 30 Tagen dauerhaft löschen, außer wenn eine 
              Aufbewahrung gesetzlich vorgeschrieben ist.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">8. Internationale Übertragungen</h2>
            <p>
              Ihre Daten werden hauptsächlich in EU-Rechenzentren gespeichert. Alle internationalen 
              Übertragungen werden mit angemessenen Schutzmaßnahmen gemäß den DSGVO-Anforderungen durchgeführt.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">9. Datenschutz für Kinder</h2>
            <p>
              Unser Service ist nicht für Kinder unter 16 Jahren bestimmt. Wir sammeln wissentlich keine 
              persönlichen Informationen von Kindern unter 16 Jahren. Wenn Sie feststellen, dass ein Kind 
              uns persönliche Informationen zur Verfügung gestellt hat, kontaktieren Sie uns bitte sofort.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">10. Änderungen an dieser Richtlinie</h2>
            <p>
              Wir können diese Datenschutzrichtlinie von Zeit zu Zeit aktualisieren. Wir werden Sie über 
              wesentliche Änderungen informieren, indem wir die neue Richtlinie auf dieser Seite veröffentlichen 
              und das Datum "Zuletzt aktualisiert" aktualisieren. Die fortgesetzte Nutzung des Services nach 
              Änderungen stellt die Annahme dar.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">11. Kontaktinformationen</h2>
            <p>
              Wenn Sie Fragen zu dieser Datenschutzrichtlinie oder unseren Datenpraktiken haben, kontaktieren Sie uns:
            </p>
            <ul className="list-none space-y-1 mt-2">
              <li><strong>E-Mail:</strong> privacy@insightflow.app</li>
              <li><strong>Adresse:</strong> InsightFlow Team, [Ihre Adresse]</li>
              <li><strong>Datenschutzbeauftragter:</strong> dpo@insightflow.app</li>
            </ul>
          </section>

          <section className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-3">Datenverantwortlicher Informationen</h2>
            <p>
              <strong>Datenverantwortlicher:</strong> InsightFlow<br/>
              <strong>Registrierung:</strong> [Ihre Geschäftsregistrierungsnummer]<br/>
              <strong>Aufsichtsbehörde:</strong> [Zuständige Datenschutzbehörde]
            </p>
          </section>
        </>
      ) : (
        // English Content
        <>
          <section>
            <h2 className="text-lg font-semibold mb-3">1. Introduction</h2>
            <p>
              InsightFlow ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
              explains how we collect, use, disclose, and safeguard your information when you use our coaching 
              application and related services (the "Service").
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">2. Information We Collect</h2>
            
            <h3 className="text-md font-medium mb-2">2.1 Personal Information</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name and email address (required for account creation)</li>
              <li>Profile information (role, specializations, preferences)</li>
              <li>Profile pictures (optional, with privacy controls)</li>
              <li>Communication preferences and language settings</li>
            </ul>

            <h3 className="text-md font-medium mb-2 mt-4">2.2 Coaching Data</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Reflection entries (thoughts, mood ratings, insights, challenges)</li>
              <li>Goals and progress tracking information</li>
              <li>Coaching session notes and schedules</li>
              <li>Messages exchanged through the platform</li>
            </ul>

            <h3 className="text-md font-medium mb-2 mt-4">2.3 Technical Information</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Device type, operating system, and browser information</li>
              <li>IP address and general location data</li>
              <li>Usage patterns and feature interactions</li>
              <li>Error logs and performance data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>To provide and maintain our coaching services</li>
              <li>To facilitate communication between coaches and coachees</li>
              <li>To track progress and generate insights</li>
              <li>To send important service updates and notifications</li>
              <li>To improve our application and develop new features</li>
              <li>To ensure security and prevent misuse</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">4. Data Sharing and Disclosure</h2>
            
            <h3 className="text-md font-medium mb-2">4.1 Coach-Coachee Relationships</h3>
            <p>
              Your coaching data (reflections, goals, progress) is shared only with coaches you have 
              explicitly linked with through our invitation system. This sharing is essential for 
              the coaching relationship.
            </p>

            <h3 className="text-md font-medium mb-2 mt-4">4.2 Third-Party Services</h3>
            <p>
              We use Supabase for data storage and authentication. Your data is stored securely in 
              EU-based servers with enterprise-grade security measures.
            </p>

            <h3 className="text-md font-medium mb-2 mt-4">4.3 Legal Requirements</h3>
            <p>
              We may disclose your information if required by law, court order, or to protect our 
              rights, safety, or the rights of others.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">5. Your Rights (GDPR Compliance)</h2>
            <p>Under GDPR and other privacy laws, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
              <li><strong>Erasure:</strong> Request deletion of your personal data</li>
              <li><strong>Portability:</strong> Receive your data in a portable format</li>
              <li><strong>Restriction:</strong> Limit how we process your data</li>
              <li><strong>Objection:</strong> Object to certain types of processing</li>
              <li><strong>Withdraw Consent:</strong> Revoke consent for data processing</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, contact us at <strong>privacy@insightflow.app</strong>
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">6. Data Security</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>End-to-end encryption for data transmission</li>
              <li>Role-based access controls (Row Level Security)</li>
              <li>Regular security audits and updates</li>
              <li>Secure EU-based data centers</li>
              <li>Multi-factor authentication support</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">7. Data Retention</h2>
            <p>
              We retain your personal data only as long as necessary for the purposes outlined in 
              this policy or as required by law. When you delete your account, we will permanently 
              delete your personal data within 30 days, except where retention is required by law.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">8. International Transfers</h2>
            <p>
              Your data is primarily stored in EU data centers. Any international transfers are 
              conducted with appropriate safeguards in accordance with GDPR requirements.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">9. Children's Privacy</h2>
            <p>
              Our service is not intended for children under 16. We do not knowingly collect 
              personal information from children under 16. If you become aware that a child has 
              provided us with personal information, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any 
              material changes by posting the new policy on this page and updating the "Last 
              updated" date. Continued use of the service after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold mb-3">11. Contact Information</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul className="list-none space-y-1 mt-2">
              <li><strong>Email:</strong> privacy@insightflow.app</li>
              <li><strong>Address:</strong> InsightFlow Team, [Your Address]</li>
              <li><strong>Data Protection Officer:</strong> dpo@insightflow.app</li>
            </ul>
          </section>

          <section className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h2 className="text-lg font-semibold mb-3">Data Controller Information</h2>
            <p>
              <strong>Data Controller:</strong> InsightFlow<br/>
              <strong>Registration:</strong> [Your business registration number]<br/>
              <strong>Supervisory Authority:</strong> [Relevant data protection authority]
            </p>
          </section>
        </>
      )}
    </div>
  )
}

export default PrivacyPolicyContent 